<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const { obterBadgeNeutro } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const listas = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  nomeLista: '',
  permitePersonalizacao: 'true',
  exclusivaCliente: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const podeVerAcoes = computed(() => true)

const colunas = [
  { field: 'idListaClienteCabecalho', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nomeLista', label: 'Nome da Lista', sortable: true, class: 'fw-medium', slotName: 'nome' },
  { field: 'descricaoLista', label: 'Descrição', sortable: true, slotName: 'descricao' },
  { field: 'prefixoItemLista', label: 'Prefixo', width: 120, sortable: true, slotName: 'prefixo' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else { listas.value = []; paginacao.value.TotalCount = 0 }
})

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    listas.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.nomeLista) params.append('nomeLista', filtros.nomeLista)
    if (filtros.permitePersonalizacao !== '') params.append('permitePersonalizacao', filtros.permitePersonalizacao)
    if (filtros.exclusivaCliente !== '') params.append('exclusivaCliente', filtros.exclusivaCliente)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/ListasCliente?${params.toString()}`)
    listas.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar listas:', error)
    erro.value = 'Não foi possível carregar os dados das listas personalizadas.'
    listas.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.nomeLista = ''; filtros.permitePersonalizacao = ''; filtros.exclusivaCliente = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarLista = () => { emit('adicionar-item') }

const editarLista = (lista) => { emit('editar-item', lista) }

const abrirAuditoria = (lista) => {
  auditoriaSelecionada.id = lista.idListaCliente || lista.idListaClienteCabecalho
  auditoriaSelecionada.nome = lista.nomeLista
  mostrarAuditoria.value = true
}

onMounted(() => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Listas Personalizadas"
    subtitulo="Gestão de listas auxiliares para cadastros e filtros"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarLista"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="podeVerAcoes"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-nome" class="form-label">Nome ou Prefixo</label>
          <input type="text" id="filtro-nome" class="form-control" v-model="filtros.nomeLista" @keyup.enter.prevent="aplicarFiltros" placeholder="Buscar..." autocomplete="off">
        </div>
        <div class="col-md-3">
          <label for="filtro-pers" class="form-label">Personalizável</label>
          <select id="filtro-pers" class="form-select" v-model="filtros.permitePersonalizacao" @change="aplicarFiltros">
            <option value="">Todas</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        </div>
        <div class="col-md-3">
          <label for="filtro-excl" class="form-label">Exclusiva do Cliente</label>
          <select id="filtro-excl" class="form-select" v-model="filtros.exclusivaCliente" @change="aplicarFiltros">
            <option value="">Todas</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        </div>
        <div class="col-auto flex-grow-1 d-flex justify-content-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="listas"
      :loading="loading"
      empty-message="Nenhum registo encontrado para estes filtros."
      row-key="idListaClienteCabecalho"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarLista($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idListaCliente || row.idListaClienteCabecalho }}</span>
      </template>

      <template #nome="{ row }">
        <i v-if="row.exclusivaCliente" class="bi bi-star-fill text-warning me-1 small" title="Exclusiva do Cliente"></i>
        <i v-if="!row.permitePersonalizacao" class="bi bi-lock-fill text-body-tertiary me-1 small" title="Protegida pelo Sistema"></i>
        {{ row.nomeLista }}
      </template>

      <template #descricao="{ row }">
        {{ row.descricaoLista }}
      </template>

      <template #prefixo="{ row }">
        <span :class="obterBadgeNeutro()">{{ row.prefixoItemLista }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" :title="row.permitePersonalizacao ? 'Editar' : 'Visualizar'" @click="editarLista(row)">
            <i :class="row.permitePersonalizacao ? 'bi bi-pencil-fill' : 'bi bi-eye-fill'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ListasClienteCabecalho"
    contexto="tenant"
    :chave-primaria="auditoriaSelecionada.id"
    :tabelas-filhas="['ListasClienteItens']"
    chave-estrangeira="IdListaClienteCabecalho"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
